import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import HeadMeta from '../components/HeadMeta';
import Lightbox from '../components/Lightbox';
import ScrollReveal from '../components/ScrollReveal';
import { useScrollZoom } from '../hooks/useScrollZoom';

export default function ExperienceDetail() {
  const { slug } = useParams();
  const { lang, t, getLocalized } = useLanguage();
  const [experience, setExperience] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImgIndex, setActiveImgIndex] = useState(0);
  const heroZoom = useScrollZoom({ baseScale: 1.15, maxScale: 1.45, zoomSpeed: 0.0006 });

  useEffect(() => {
    setLoading(true);

    // Fetch all experiences and match slug
    fetch('https://api.dev.auxo.dk/public/v2/content/experiences')
      .then((res) => res.json())
      .then((data) => {
        if (data.items) {
          const match = data.items.find((item) => {
            const daSlug = typeof item.experience_slug === 'object' ? item.experience_slug?.da : item.experience_slug;
            const enSlug = typeof item.experience_slug === 'object' ? item.experience_slug?.en : item.experience_slug;
            const itemSlugDa = typeof item.slug === 'object' ? item.slug?.da : item.slug;
            const itemSlugEn = typeof item.slug === 'object' ? item.slug?.en : item.slug;
            const itemSlugDe = typeof item.slug === 'object' ? item.slug?.de : item.slug;
            const itemSlugNo = typeof item.slug === 'object' ? item.slug?.no : item.slug;
            const itemSlugSv = typeof item.slug === 'object' ? item.slug?.sv : item.slug;

            return daSlug === slug || 
                   enSlug === slug || 
                   item.experience_slug === slug || 
                   item.id === slug ||
                   itemSlugDa === slug || 
                   itemSlugEn === slug ||
                   itemSlugDe === slug ||
                   itemSlugNo === slug ||
                   itemSlugSv === slug;
          });
          const activeItem = match || data.items[0];
          setExperience(activeItem);

          if (activeItem.gallery_images && activeItem.gallery_images.length > 0) {
            setGalleryImages(activeItem.gallery_images);
          } else {
            // Technical gallery slug lookup (strict match, no general fallback)
            const gallerySlug = activeItem ? (activeItem.experience_slug?.da || activeItem.experience_slug || slug) : slug;
            fetch('https://api.dev.auxo.dk/public/v2/content/galleries')
              .then((gRes) => gRes.json())
              .then((gData) => {
                if (gData.items) {
                  const foundGal = gData.items.find((g) => g.gallery_slug === gallerySlug || g.slug === gallerySlug);
                  if (foundGal && foundGal.images && foundGal.images.length > 0) {
                    setGalleryImages(foundGal.images);
                  } else {
                    const fallbackImgs = [activeItem.hero_image?.[0], activeItem.card_image?.[0]].filter(Boolean);
                    setGalleryImages(fallbackImgs);
                  }
                }
              })
              .catch((err) => console.error('Gallery fetch error:', err));
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Experience fetch error:', err);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <div className="flex items-center gap-3">
          <i className="fa-solid fa-spinner fa-spin text-2xl text-cyan-500" aria-hidden="true" />
          <span>{t('experienceDetail.loading') || 'Henter oplevelse...'}</span>
        </div>
      </div>
    );
  }

  if (!experience) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-white px-4">
        <h1 className="text-2xl font-bold mb-4 uppercase">{t('experienceDetail.notFound') || 'Oplevelsen blev ikke fundet'}</h1>
        <Link to="/oplevelser" className="px-6 py-3 rounded-full bg-cyan-600 text-white font-bold text-sm">
          {t('experiences.viewAll')}
        </Link>
      </div>
    );
  }

  const title = getLocalized(experience.title);
  const subtitle = getLocalized(experience.subtitle);
  const descriptionHtml = getLocalized(experience.description);
  const highlightsHtml = getLocalized(experience.highlights);
  const heroImg = experience.hero_image?.[0] || 'https://assets.auxodev.dk/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/hero-redsea.jpg';
  const price = getLocalized(experience.price_from);
  const duration = getLocalized(experience.duration);
  const groupSize = getLocalized(experience.group_size);
  const serviceText = getLocalized(experience.service) || 'Dansk & Engelsk support';

  const openLightbox = (index) => {
    setActiveImgIndex(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <HeadMeta 
        title={`${title} - Captain Hurghada Tours`}
        description={`${subtitle}. Oplev ${title} i Hurghada med personlig tryghed, lokal ekspertise og transfer.`}
      />

      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center justify-center pt-28 sm:pt-36 lg:pt-40 pb-16 bg-slate-950 text-white overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={`https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/${heroImg.replace('https://assets.auxodev.dk/', '')}`}
            alt={title}
            style={{
              transform: `scale(${heroZoom.scale}) translate3d(0, ${heroZoom.translateY}px, 0)`,
              transition: 'transform 0.1s cubic-bezier(0.1, 1, 0.1, 1)',
              willChange: 'transform',
            }}
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent pointer-events-none" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <ScrollReveal variant="up" duration={700}>
            <Link
              to="/oplevelser"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-200 hover:text-white transition-colors mb-6 bg-slate-900/60 backdrop-blur-md px-4 py-2 rounded-full border border-white/10"
            >
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              <span>{t('experienceDetail.back')}</span>
            </Link>
          </ScrollReveal>

          <ScrollReveal variant="up" delay={150} duration={800}>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4 uppercase">
              {title}
            </h1>
          </ScrollReveal>

          <ScrollReveal variant="up" delay={250} duration={800}>
            <p className="text-base sm:text-lg text-slate-200 font-normal max-w-2xl mx-auto mb-8 leading-relaxed">
              {subtitle}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Quick specs bar beneath hero (Matching Live Site) */}
      <section className="bg-white border-b border-slate-200 py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {price && (
              <div>
                <span className="block text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-1">{t('experiences.fromPrice')}</span>
                <span className="text-sm sm:text-base font-bold text-cyan-600">{price}</span>
              </div>
            )}
            {duration && (
              <div>
                <span className="block text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-1">{t('experiences.duration')}</span>
                <span className="text-sm sm:text-base font-bold text-slate-900">{duration}</span>
              </div>
            )}
            {groupSize && (
              <div>
                <span className="block text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-1">{t('experiences.groupSize')}</span>
                <span className="text-sm sm:text-base font-bold text-slate-900">{groupSize}</span>
              </div>
            )}
            {serviceText && (
              <div>
                <span className="block text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-1">Service</span>
                <span className="text-sm sm:text-base font-bold text-cyan-600">{serviceText}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Details Section */}
      <section className="py-16 bg-[#FAF9F5] relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            
            {/* Left 8 Cols: Description, Highlights, Gallery */}
            <div className="lg:col-span-8 space-y-8">
              
              {/* Description */}
              {descriptionHtml && (
                <div className="bg-white rounded-3xl p-8 sm:p-10 border border-slate-100 shadow-sm space-y-4">
                  <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-slate-900 mb-4">
                    {t('experienceDetail.about')}
                  </h2>
                  <div 
                    className="prose prose-slate max-w-none text-slate-700 leading-relaxed text-sm sm:text-base space-y-4"
                    dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                  />
                </div>
              )}

              {/* Highlights & Inclusions */}
              {highlightsHtml && (
                <div className="bg-white rounded-3xl p-8 sm:p-10 border border-slate-100 shadow-sm space-y-4">
                  <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-slate-900 mb-4">
                    {t('experienceDetail.highlights')}
                  </h2>
                  <div 
                    className="prose prose-slate max-w-none text-slate-700 leading-relaxed text-sm sm:text-base space-y-3"
                    dangerouslySetInnerHTML={{ __html: highlightsHtml }}
                  />
                </div>
              )}

              {/* Photo Gallery Grid */}
              {galleryImages && galleryImages.length > 0 && (
                <div className="bg-white rounded-3xl p-8 sm:p-10 border border-slate-100 shadow-sm space-y-6">
                  <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-slate-900">
                    {t('experienceDetail.photos')}
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {galleryImages.map((imgUrl, i) => (
                      <div 
                        key={i}
                        onClick={() => openLightbox(i)}
                        className="aspect-square rounded-2xl overflow-hidden cursor-pointer group relative bg-slate-900 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <img
                          src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,height=400,fit=cover,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                          alt={`${title} galleri ${i + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-slate-950/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <i className="fa-solid fa-expand text-white text-xl" aria-hidden="true" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>

            {/* Right Sidebar (4 cols): Booking CTA & Contact */}
            <div className="lg:col-span-4 space-y-6">
              
              <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-xl sticky top-28 space-y-6">
                <div className="text-center pb-6 border-b border-slate-100">
                  <span className="text-xs uppercase font-extrabold text-cyan-600 tracking-wider block mb-1">
                    {t('experienceDetail.book')}
                  </span>
                  <h3 className="text-xl font-extrabold text-slate-900 uppercase tracking-tight mb-2">
                    {title}
                  </h3>
                  {price && (
                    <div className="text-2xl font-extrabold text-slate-900">
                      {price}
                    </div>
                  )}
                </div>

                <div className="space-y-3 text-xs sm:text-sm text-slate-700">
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-check text-cyan-600 shrink-0" aria-hidden="true" />
                    <span>{t('experienceDetail.foundedBy')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-check text-cyan-600 shrink-0" aria-hidden="true" />
                    <span>{t('experienceDetail.transferIncluded')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-check text-cyan-600 shrink-0" aria-hidden="true" />
                    <span>{t('experienceDetail.personalSupport')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-check text-cyan-600 shrink-0" aria-hidden="true" />
                    <span>{t('experienceDetail.noPrepayment')}</span>
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <Link
                    to="/kontakt"
                    className="w-full py-3.5 px-4 rounded-xl bg-slate-950 hover:bg-slate-800 text-white font-semibold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-md"
                  >
                    <span>{t('experienceDetail.sendInquiry')}</span>
                    <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                  </Link>
                  <a
                    href="https://instagram.com/captainhurgada"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3 px-4 rounded-xl bg-slate-950 hover:bg-slate-800 text-white font-semibold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2"
                  >
                    <i className="fa-brands fa-instagram text-sm" aria-hidden="true" />
                    <span>{t('experienceDetail.contactInsta')}</span>
                  </a>
                </div>

                <div className="pt-4 border-t border-slate-100 text-center">
                  <p className="text-[11px] text-slate-500">
                    {t('experienceDetail.questionsCall')}{' '}
                    <a href="tel:+201104354994" className="font-bold text-slate-900 hover:text-cyan-600 underline">
                      +20 110 435 4994
                    </a>
                  </p>
                </div>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={galleryImages}
          initialIndex={activeImgIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}
